import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, MapPin, Clock, LogIn, LogOut, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, isToday, parseISO } from 'date-fns';

// Mock data for simulation
const MOCK_CHECKINS = [
  { id: '1', user_name: 'John Smith', site_name: 'Site Alpha', check_in_time: new Date(Date.now() - 3600000).toISOString(), check_out_time: null },
  { id: '2', user_name: 'Sarah Johnson', site_name: 'Main Office', check_in_time: new Date(Date.now() - 7200000).toISOString(), check_out_time: new Date(Date.now() - 1800000).toISOString() },
  { id: '3', user_name: 'Mike Williams', site_name: 'Site Alpha', check_in_time: new Date(Date.now() - 10800000).toISOString(), check_out_time: null },
  { id: '4', user_name: 'Emily Davis', site_name: 'Warehouse B', check_in_time: new Date(Date.now() - 14400000).toISOString(), check_out_time: new Date(Date.now() - 3600000).toISOString() },
  { id: '5', user_name: 'Tom Brown', site_name: 'Distribution Center', check_in_time: new Date(Date.now() - 18000000).toISOString(), check_out_time: null },
];

export function SiteCheckinsTodayTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setCheckins(MOCK_CHECKINS);
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadCheckins = async () => {
      try {
        setLoading(true);
        const today = new Date().toISOString().split('T')[0];

        const { data, error } = await supabase
          .from('site_checkins')
          .select(`
            id,
            check_in_time,
            check_out_time,
            user_profiles!site_checkins_user_id_fkey(first_name, last_name),
            site_locations!site_checkins_site_id_fkey(name)
          `)
          .eq('company_id', company.id)
          .gte('check_in_time', today)
          .order('check_in_time', { ascending: false })
          .limit(20);

        if (error) throw error;

        const formattedData = (data || []).map(c => ({
          id: c.id,
          user_name: `${c.user_profiles?.first_name || ''} ${c.user_profiles?.last_name || ''}`.trim(),
          site_name: c.site_locations?.name || 'Unknown Site',
          check_in_time: c.check_in_time,
          check_out_time: c.check_out_time
        }));

        setCheckins(formattedData);
      } catch (error) {
        console.error('Failed to load checkins:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCheckins();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('site-checkins-today')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'site_checkins',
        filter: `company_id=eq.${company.id}`
      }, () => loadCheckins())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [company?.id, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  const activeCheckins = checkins.filter(c => !c.check_out_time);
  const completedCheckins = checkins.filter(c => c.check_out_time);

  if (checkins.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <MapPin className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">No check-ins today</p>
        <p className="text-xs text-gray-500 mt-1">Check-ins will appear here</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-500 text-white">{activeCheckins.length}</Badge>
          <span className="text-xs text-gray-600">on site</span>
        </div>
        <span className="text-[10px] text-gray-500">
          {completedCheckins.length} completed
        </span>
      </div>

      {/* Checkins list */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-2 pr-2">
          {checkins.map((checkin) => {
            const isActive = !checkin.check_out_time;

            return (
              <div
                key={checkin.id}
                className={cn(
                  "p-2 rounded-lg border transition-all",
                  isActive ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"
                )}
              >
                <div className="flex items-start gap-2">
                  {/* Avatar */}
                  <div className={cn(
                    "w-7 h-7 rounded-full flex items-center justify-center shrink-0",
                    isActive ? "bg-emerald-500" : "bg-gray-400"
                  )}>
                    <User className="h-3.5 w-3.5 text-white" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">
                      {checkin.user_name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <MapPin className="h-2.5 w-2.5 text-gray-400" />
                      <span className="text-[10px] text-gray-600 truncate">
                        {checkin.site_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
                      <span className="flex items-center gap-0.5">
                        <LogIn className="h-2.5 w-2.5 text-emerald-500" />
                        {format(parseISO(checkin.check_in_time), 'HH:mm')}
                      </span>
                      {checkin.check_out_time && (
                        <span className="flex items-center gap-0.5">
                          <LogOut className="h-2.5 w-2.5 text-gray-400" />
                          {format(parseISO(checkin.check_out_time), 'HH:mm')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  {isActive && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px] shrink-0">
                      Active
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Footer */}
      <button
        onClick={() => navigate('/checkins')}
        className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 font-medium transition-colors shrink-0"
      >
        <MapPin className="h-3.5 w-3.5" />
        <span>View All Check-ins</span>
      </button>
    </div>
  );
}
